// The admin page's store section: the database's size and growth per table, the retention rules
// and the last nightly clean-up.
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { formatFileSize, formatWibLong, type RunPart } from "@po/shared";
import { api } from "../lib/api";
import { Parts } from "./SyncMonitor";

type Store = {
  at: string;
  totalBytes: number;
  total7dAgo: { day: string; bytes: number } | null;
  total30dAgo: { day: string; bytes: number } | null;
  daily: { day: string; bytes: number }[];
  tables: { name: string; bytes: number; rows: number; bytes7dAgo: number | null; bytes30dAgo: number | null; rows30dAgo: number | null }[];
  lastCleanUp: { at: string; parts: RunPart[] } | null;
};

/** "+1.2 MB" / "−300 KB" / "—" when there is no sample to compare with. */
function growth(now: number, then: number | null | undefined) {
  if (then == null) return <span className="text-muted">—</span>;
  const d = now - then;
  if (d === 0) return <span className="text-muted">0</span>;
  return <span style={{ color: d > 0 ? "var(--ink)" : "var(--stage-received)" }}>{`${d > 0 ? "+" : "−"}${formatFileSize(Math.abs(d))}`}</span>;
}

const RETENTION = [
  "Sync runs: 30 days, except those the budget change log is dated by and each module's latest run and latest success",
  "Finished jobs: 1 day",
  "Audit events: 1 year",
  "Sessions: 1 h idle or 12 h old",
  "Sign-in limits: 1 day",
  "Size samples: 400 days",
];

export function StoreSize() {
  const q = useQuery({ queryKey: ["admin-store"], queryFn: () => api<Store>("/admin/store") });
  const [all, setAll] = useState(false);
  if (q.isError) return <p className="text-[12.5px] text-[var(--danger)]">Could not load the store size.</p>;
  const d = q.data;
  if (!d) return <p className="text-[12.5px] text-muted">Loading…</p>;
  const shown = all ? d.tables : d.tables.slice(0, 15);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 text-[13px]">
        <span>
          <span className="font-semibold">Database:</span> <span className="tnum">{formatFileSize(d.totalBytes)}</span>
        </span>
        <span>
          7 days: <span className="tnum">{growth(d.totalBytes, d.total7dAgo?.bytes)}</span>
        </span>
        <span>
          30 days: <span className="tnum">{growth(d.totalBytes, d.total30dAgo?.bytes)}</span>
        </span>
        <button className="text-brand hover:underline text-[12.5px] ml-auto" onClick={() => void q.refetch()} disabled={q.isFetching}>
          {q.isFetching ? "Refreshing…" : "Refresh"}
        </button>
      </div>
      {!d.total30dAgo && <p className="text-[11.5px] text-muted -mt-3">Growth is measured from the nightly size samples; the figures fill in as nights pass ({d.daily.length} so far).</p>}
      <div className="overflow-x-auto">
        <table className="text-[12.5px] min-w-[560px] w-full">
          <thead>
            <tr className="text-muted text-left">
              <th className="pr-4 font-normal">Table</th>
              <th className="pr-4 font-normal text-right">Size</th>
              <th className="pr-4 font-normal text-right">Rows (est.)</th>
              <th className="pr-4 font-normal text-right">7 days</th>
              <th className="pr-4 font-normal text-right">30 days</th>
              <th className="font-normal text-right">Rows, 30 days</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((t) => (
              <tr key={t.name} className="border-t border-line">
                <td className="pr-4 py-0.5">{t.name}</td>
                <td className="pr-4 text-right tnum">{formatFileSize(t.bytes)}</td>
                <td className="pr-4 text-right tnum">{t.rows.toLocaleString()}</td>
                <td className="pr-4 text-right tnum">{growth(t.bytes, t.bytes7dAgo)}</td>
                <td className="pr-4 text-right tnum">{growth(t.bytes, t.bytes30dAgo)}</td>
                <td className="text-right tnum">
                  {t.rows30dAgo == null ? <span className="text-muted">—</span> : `${t.rows - t.rows30dAgo >= 0 ? "+" : "−"}${Math.abs(t.rows - t.rows30dAgo).toLocaleString()}`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {d.tables.length > 15 && (
        <button className="text-brand hover:underline text-[12.5px]" onClick={() => setAll(!all)}>
          {all ? "Show the 15 largest" : `Show all ${d.tables.length} tables`}
        </button>
      )}
      <div>
        <div className="label mb-1">Retention (nightly clean-up)</div>
        <ul className="text-[12.5px] list-disc pl-5 space-y-0.5">
          {RETENTION.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      </div>
      <div>
        <div className="label mb-1">Last clean-up</div>
        {d.lastCleanUp ? (
          <>
            <p className="text-[12px] text-muted mb-1">{formatWibLong(new Date(d.lastCleanUp.at))} · rows deleted per step</p>
            <Parts parts={d.lastCleanUp.parts} />
          </>
        ) : (
          <p className="text-muted text-[12.5px]">None recorded yet: the next nightly run records it.</p>
        )}
      </div>
    </div>
  );
}
